import { SelectInput } from '@/components/form/SelectInput';
import { Label } from '@/components/Label';
import { Modal } from '@/components/shared/modal/Modal';
import { dateToApiFormat } from '@/helpers/dateHelper';
import { handleMutationResult } from '@/helpers/toastHelper';
import { useCreateRosterMutation } from '@/services/gql/graphql.generated';
import { useTemplatesForLocation } from '@/services/shared/useTemplatesForLocation';
import { useMyLocation } from '@/services/useLocations';
import { FC, Suspense, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRefreshRosters } from '../providers/RosterOptionsService';
import strings from '../roster.translations';

interface RosterCreateModalProps {
  open: boolean;
  onClose: () => void;
  date: Date;
}

export const RosterCreateModalContent: FC<RosterCreateModalProps> = ({ onClose, date, open }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);

  const { templateOptions } = useTemplatesForLocation();
  const dropdownOptions = useMemo(() => {
    return templateOptions.map(t => ({ name: t.name, value: t.id }));
  }, [templateOptions]);

  const [selectedTemplateId, setSelectedTemplateId] = useState<string | undefined>();

  useEffect(() => {
    if (!selectedTemplateId) {
      const defaultTemplate = templateOptions.find(t => t.default);

      if (defaultTemplate) {
        setSelectedTemplateId(defaultTemplate.id);
      }
    }
  }, [selectedTemplateId, templateOptions]);

  const { id: locationId } = useMyLocation();
  const [, createRoster] = useCreateRosterMutation();
  const refreshRosters = useRefreshRosters();

  const onCreate = async () => {
    if (!selectedTemplateId) {
      throw Error('Template must be selected');
    }

    setLoading(true);
    const result = await createRoster({ date: dateToApiFormat(date), templateId: selectedTemplateId, locationId });

    await handleMutationResult(result, {
      successMessage: "Roster successfully created.",
      errorMessage: "Unable to create roster.",
      onSuccess: async data => {
        await refreshRosters();

        onClose();
        if (data?.createRosterFromTemplate?.id) {
          setTimeout(() => navigate(`/rosters/${dateToApiFormat(date)}`), 0);
        }
        setLoading(false);
      },
      onError: () => {
        setLoading(false);
      }
    });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      header={strings.createRosterModal.title}
      customActions={[
        { callback: onCreate, primary: true, label: strings.create, loading: loading, disabled: !selectedTemplateId }
      ]}
      className="space-y-4"
    >
      <div>{strings.createRosterModal.createMessage(date)}</div>
      <div className="flex flex-col gap-2">
        <Label>{strings.optimiseRosters.selectTemplate}</Label>
        <SelectInput options={dropdownOptions} value={selectedTemplateId} onChange={setSelectedTemplateId} />
      </div>
    </Modal>
  );
};

export const RosterCreateModal: FC<RosterCreateModalProps> = ({ open, onClose, date }) => {
  return (
    <Suspense>
      <RosterCreateModalContent onClose={onClose} date={date} open={open} />
    </Suspense>
  );
};
